import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    NoticeAutoCloseDelayConstantType,
    NoticeActionsOffsetConstantType,
    NoticeAnnouncementConstantType,
    NoticeActionRadiusConstantType,
    NoticeLiveRegionConstantType,
    NoticeIconColorsConstantType,
    NoticeDynamicStyleType,
    NoticeAutoCloseType,
    NoticeIconColors,
    NoticeType
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getSurfaceStyle
} from "../../helpers/theme/elevation";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getHitArea
} from "../../helpers/theme/control";
import {
    webStyle
} from "../../utils";

export const NOTICE_ICON_COLORS: Record<NoticeType, keyof NCoreUIKit.IconContentColors> = {
    primary: "emphasized",
    neutral: "default",
    success: "success",
    warning: "warning",
    danger: "danger",
    info: "info"
};

export const NOTICE_AUTO_CLOSE: NoticeAutoCloseType = {
    duration: "autoplay",
    wordsPerDuration: 9,
    maximumRatio: 2
};

export const getNoticeIconColors = ({
    colors,
    type
}: NoticeIconColorsConstantType): NoticeIconColors => {
    const iconColorKey = NOTICE_ICON_COLORS[type];

    return {
        iconColor: colors.content.icon[iconColorKey],
        iconColorKey
    };
};

export const getNoticeAutoCloseDelay = ({
    durations,
    title,
    body
}: NoticeAutoCloseDelayConstantType): number => {
    const words = `${title ?? ""} ${body ?? ""}`.split(/\s+/).filter((word) => word.length > 0);

    const duration = durations[NOTICE_AUTO_CLOSE.duration];

    const readingTime = Math.round(words.length * duration / NOTICE_AUTO_CLOSE.wordsPerDuration);

    return Math.min(Math.max(readingTime, duration), duration * NOTICE_AUTO_CLOSE.maximumRatio);
};

export const getNoticeAnnouncement = ({
    title,
    body
}: NoticeAnnouncementConstantType): string => {
    return body ? `${title}, ${body}` : title;
};

export const getNoticeLiveRegionStyle = ({
    borders
}: NoticeLiveRegionConstantType): ViewStyle => {
    return {
        height: borders.line,
        width: borders.line
    };
};

export const getNoticeActionRadius = ({
    controlHeights,
    typography,
    isCompact,
    radiuses,
    borders,
    spaces
}: NoticeActionRadiusConstantType): number => {
    const titleOffset = (typography.titleSmallSize.lineHeight - controlHeights.small) / 2;

    return concentric({
        inset: isCompact ? spaces.spacingSm + borders.line : spaces.spacingMd + titleOffset + borders.line,
        outer: radiuses.surface,
        role: radiuses.actions
    });
};

export const getNoticeActionsOffset = ({
    controlHeights,
    touchTargets,
    pointerType,
    typography,
    isCompact,
    spaces
}: NoticeActionsOffsetConstantType): number => {
    const hitArea = getHitArea({
        visualSize: controlHeights.small,
        touchTargets,
        pointerType
    });

    if(isCompact) {
        return Math.min(0, (controlHeights.large - spaces.spacingXs * 2 - hitArea.minHeight) / 2);
    }

    return (typography.titleSmallSize.lineHeight - hitArea.minHeight) / 2;
};

const stylesheet = StyleSheet.create({
    liveRegion: {
        pointerEvents: "none",
        position: "absolute",
        overflow: "hidden",
        ...webStyle({
            clipPath: "inset(50%)",
            whiteSpace: "nowrap"
        })
    },
    actionsContainer: {
        justifyContent: "flex-end",
        flexDirection: "row",
        alignItems: "center",
        marginStart: "auto",
        flexWrap: "wrap",
        flexShrink: 1
    },
    iconContainer: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    contentContainer: {
        flexDirection: "column",
        flexShrink: 1,
        flexGrow: 1,
        minWidth: 0
    },
    surface: {
        flexDirection: "row",
        borderStyle: "solid",
        flexWrap: "wrap"
    }
});

export const useStyles = ({
    controlPaddings,
    isActionVisible,
    isCustomContent,
    controlHeights,
    shapeConfigs,
    touchTargets,
    pointerType,
    shadowAlpha,
    elevations,
    typography,
    iconSizes,
    isCompact,
    radiuses,
    borders,
    colors,
    spaces
}: NoticeDynamicStyleType) => {
    const styles = {
        iconContainer: {
            height: typography.titleSmallSize.lineHeight,
            width: iconSizes.small
        } as Mutable<ViewStyle>,
        actionsContainer: {
            marginVertical: getNoticeActionsOffset({
                controlHeights,
                touchTargets,
                pointerType,
                typography,
                isCompact,
                spaces
            }),
            gap: controlPaddings.small
        } as Mutable<ViewStyle>,
        surface: {
            ...getSurfaceStyle({
                surface: "toast",
                shadowAlpha,
                elevations,
                borders,
                colors
            }),
            ...getCornerStyle({
                radius: radiuses.surface,
                shapeConfigs
            }),
            gap: controlPaddings.small
        } as Mutable<ViewStyle>,
        contentContainer: {
        } as Mutable<ViewStyle>
    };

    if(isCompact) {
        styles.surface.alignContent = "center";
        styles.surface.alignItems = "center";
        styles.actionsContainer.gap = spaces.spacingXs;
    } else {
        styles.surface.alignItems = "flex-start";
    }

    if(isCustomContent) {
        return styles;
    }

    if(isCompact) {
        styles.surface.paddingEnd = isActionVisible ? spaces.spacingSm : spaces.spacingMd;
        styles.surface.minHeight = controlHeights.large;
        styles.surface.paddingVertical = spaces.spacingXs;
        styles.surface.paddingStart = spaces.spacingMd;

        return styles;
    }

    styles.surface.padding = spaces.spacingMd;

    return styles;
};
export default stylesheet;
